import { Component, OnDestroy, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  combineLatest,
  filter,
  finalize,
  first,
  map,
  merge,
  of,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { AssignmentStatuses } from 'src/app/core/enums/assignment-statuses.enum';
import { AssignmentFormModel } from 'src/app/core/models/project/assignment/assignment-form.model';
import { AssignmentService } from 'src/app/core/services/assignment.service';
import { Convertor } from 'src/app/core/utils/convertor';
import { AssignmentForUpdate } from 'src/app/core/models/project/assignment/assignment.model';
import { Util } from 'src/app/core/utils/util';
import { AssignmentOverview } from 'src/app/core/models/project/assignment/assignment-overview.interface';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EditableAssignmentComponent } from '../editable-assignment.component';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmModalComponent } from 'src/app/core/components/confirm-modal/confirm-modal.component';
import { ReorderedEnums } from 'src/app/core/utils/reordered-enums';
import { MinimalProfile } from 'src/app/core/models/user/profile';
import { DeActivatable } from 'src/app/core/interfaces/de-activatable.interface';
import { TranslateService } from '@ngx-translate/core';
import { AssignmentFollowService } from 'src/app/core/services/assignment-follow.service';
import { AssignmentChangeHub } from 'src/app/core/signalr-hubs/assignment-change.hub';
import { ObservableUtil } from 'src/app/core/utils/observable-util';
import { AssignmentPriorities } from 'src/app/core/enums/assignment-priorities.enum';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { PermissionService } from 'src/app/core/services/permission.service';
import { TabItem } from 'src/app/core/models/tabs/tab-item.interface';

@Component({
    selector: 'app-project-assignment-overview',
    templateUrl: './assignment-overview.component.html',
    styleUrls: ['./assignment-overview.component.scss'],
    providers: [AssignmentFormModel],
    standalone: false
})
export class AssignmentOverviewComponent
  extends EditableAssignmentComponent
  implements OnDestroy, DeActivatable
{
  readonly employeeService = inject(EmployeeService);
  private readonly assignmentService = inject(AssignmentService);
  private readonly followService = inject(AssignmentFollowService);
  private readonly permissionService = inject(PermissionService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly modalService = inject(NgbModal);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly assignmentChangeHub = inject(AssignmentChangeHub);

  public readonly assignmentPriorityBadgeClassMap: Record<number, string> = {
    [AssignmentPriorities.Critical]: 'assignment-priority-mark--critical',
    [AssignmentPriorities.High]: 'assignment-priority-mark--high',
    [AssignmentPriorities.Middle]: 'assignment-priority-mark--middle',
    [AssignmentPriorities.Low]: 'assignment-priority-mark--low',
  };

  public readonly discussionTabs = {
    conversation: 'conversation',
    reports: 'reports',
  } as const;

  public activeDiscussionTab: 'conversation' | 'reports' = 'conversation';
  public readonly discussionTabItems: TabItem[] = [
    {
      id: 'conversation',
      labelKey: 'COMPONENTS.ASSIGNMENT_OVERVIEW.TABS.CONVERSATION',
      icon: 'message-square',
    },
    {
      id: 'reports',
      labelKey: 'COMPONENTS.ASSIGNMENT_OVERVIEW.TABS.REPORTS',
      icon: 'chart-bar',
    },
  ];

  private hasUnsavedChanges = false;
  private unsubscribe$ = new Subject<void>();

  public readonly notFound = Constants.NotFound;
  public readonly uaDateWithTimeFormat = Constants.DateFormats.get('ua-short');
  public readonly uaDateFormat = Constants.DateFormats.get('ua-date');
  public readonly assignmentStatuses = AssignmentStatuses;
  public readonly assignmentPriorities = AssignmentPriorities;
  public readonly tags = Constants.ProjectTags;
  public readonly utility = Util;
  public readonly currentLang: string;

  public readonly orderedStatuses = ReorderedEnums.enumMap.get(
    Constants.AssignmentStatuses,
  );

  public readonly orderedPriorities = ReorderedEnums.enumMap.get(
    Constants.AssignmentPriorities,
  );

  public readonly emptyDescriptionParam: Record<string, string> = {
    uk: 'описі задачі',
    en: 'Assignment description',
  };

  public readonly canEditAssignment$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Assignment,
          UserCompanyPermissions.Edit,
        ),
      ),
      shareReplay(),
    );

  public readonly canDeleteAssignment$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Assignment,
          UserCompanyPermissions.Delete,
        ),
      ),
      shareReplay(),
    );

  public isLoading = false;
  public isFollowing = false;
  public attachmentsPath = '';
  public assignmentBackUrl = '';
  public projectTagLabel = '';
  public statusFormControl: FormControl<AssignmentStatuses | null>;

  public sanitizedDescription$ = new BehaviorSubject<SafeHtml>({});

  public assignment$ = new BehaviorSubject<AssignmentOverview>({
    id: '',
    title: '',
    status: AssignmentStatuses.New,
    priority: AssignmentPriorities.Middle,
    createdAt: this.today,
    isArchived: false,
    project: { name: '', color: '' },
  });

  public defaultFormAssignmentData$ = this.assignment$.pipe(
    takeUntil(this.unsubscribe$),
    tap((assignment) => {
      // This manipulation needs properly set value to the 'date' input.
      // It receives only string as value for some reasons.
      let deadline = '';

      if (assignment.deadline) {
        deadline = Util.getFormattedStringDate(new Date(assignment.deadline));
      }

      this.assignmentFormModel.form.patchValue({
        title: assignment.title,
        description: assignment.description,
        deadline: deadline,
        status: assignment.status,
        priority: assignment.priority,
      });
    }),
  );

  public assignmentProducer$ = this.assignment$.pipe(
    filter((a) => !!a.producer),
    switchMap((a) =>
      ObservableUtil.getProfileWithImage$(a.producer!, this.fileService),
    ),
  );

  constructor() {
    super();
    const route = inject(ActivatedRoute);
    const employeeService = this.employeeService;


    this.isLoading = true;

    this.statusFormControl = this.assignmentFormModel.form.controls.status;
    this.currentLang =
      this.translateService.currentLang ?? Constants.DefaultLanguage;

    const initUrls$ = route.params.pipe(
      takeUntil(this.unsubscribe$),
      tap((params) => {
        this.companyId = route.parent?.parent?.snapshot.params['id'];
        this.projectId = params['projectId'];
        this.assignmentBackUrl = `/companies/${this.companyId}/projects/${this.projectId}`;

        this.assignmentService.setAssignmentsUrl(this.projectId);
        this.followService.setAssignmentFollowsUrl(this.projectId);
        employeeService.setEmployeesUrl(this.companyId);
      }),
      shareReplay(1),
    );

    initUrls$
      .pipe(
        switchMap(() =>
          employeeService
            .getProjectEmployeesLookup(this.projectId)
            .pipe(
              tap((employees) =>
                this.projectEmployees$.next(
                  employees.filter((e) => e.id !== this.assignmentEmployee?.id),
                ),
              ),
            ),
        ),
      )
      .subscribe();

    const assignment$ = initUrls$.pipe(
      switchMap((params) => this.assignmentService.getAssignment(params['id'])),
      switchMap((assignment) => {
        let imageUrl: Observable<string>;

        if (!assignment.project.titleImageExtension) {
          imageUrl = of(Constants.NotFound);
        }

        imageUrl = this.fileService.getFileUrl(
          `companies/${this.companyId}/projects/${this.projectId}/title${assignment.project.titleImageExtension}`,
        );

        return combineLatest([of(assignment), imageUrl]);
      }),
      tap(([assignment, imageUrl]) => {
        assignment.project.imageUrl = imageUrl;

        this.attachmentsPath = `companies/${this.companyId}/projects/${this.projectId}/assignments/${assignment.id}/files`;
        this.assignment$.next(assignment);

        if (assignment.description) {
          this.sanitizedDescription$.next(
            this.sanitizer.bypassSecurityTrustHtml(assignment.description),
          );
        }

        this.isLoading = false;
      }),
      map(([assignment]) => assignment),
      catchError(() => {
        this.isLoading = false;
        return EMPTY;
      }),
      shareReplay(1),
    );

    this.initEmployee$(assignment$)
      .pipe(
        tap((employee) => {
          if (employee) {
            this.assignmentEmployee = employee;
          }
        }),
      )
      .subscribe();

    this.formModel.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(() => (this.hasUnsavedChanges = this.formModel.form.dirty)),
      )
      .subscribe();

    assignment$
      .pipe(
        first(),
        switchMap((assignment) =>
          this.followService.checkFollow(assignment.id),
        ),
        tap((res) => (this.isFollowing = res)),
      )
      .subscribe();

    this.defaultFormAssignmentData$.subscribe();

    this.assignment$
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((assignment) => {
          this.projectTagLabel = assignment.project.tag
            ? (this.tags.get(assignment.project.tag) ?? '')
            : '';
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.hasUnsavedChanges) {
      const modalRef = this.modalService.open(ConfirmModalComponent, {
        size: 'sm',
      });

      modalRef.componentInstance.message = this.translateService.instant(
        `WARNINGS.UNSAVED_CHANGES`,
      );

      const modalResult$ = merge(modalRef.closed, modalRef.dismissed).pipe(
        first(),
        map((res) => !!res),
        catchError((error) => {
          console.error(error);
          return EMPTY;
        }),
      );

      return modalResult$;
    }

    return true;
  }

  public onUpdateInput(fieldName: string): void {
    this.isLoading = true;

    const formValue = this.assignmentFormModel.form.get(fieldName)?.value;
    const currentAssignment = this.assignment$.getValue()!;

    let newValue = structuredClone(formValue);

    if (fieldName === this.assignmentFormModel.fieldNames.deadline) {
      newValue = this.generateDeadline(
        this.deadlineFormControl.value,
        this.statusFormControl.value!,
      );
    }

    this.assignmentService
      .patchAssignment(currentAssignment.id, fieldName, newValue)
      .pipe(
        first(),
        tap(() => {
          const updatedAssignment = {
            ...currentAssignment,
            [fieldName]: formValue,
          };

          this.updateCurrentAssignment(updatedAssignment);

          if (fieldName === this.assignmentFormModel.fieldNames.description) {
            this.sanitizedDescription$.next(
              this.sanitizer.bypassSecurityTrustHtml(newValue),
            );
          }

          this.assignmentChangeHub.sendAssignmentChangeNotification(
            currentAssignment.id,
          );
        }),
        finalize(() => {
          this.isLoading = false;
          this.hasUnsavedChanges = false;
        }),
      )
      .subscribe();
  }

  public override onSelectEmployee(employee: MinimalProfile | null): void {
    if (!employee) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    const currentAssignment = this.assignment$.getValue();
    const fieldName = 'userId';
    const userId = employee.id;

    this.assignmentService
      .patchAssignment(currentAssignment.id, fieldName, userId)
      .pipe(
        first(),
        tap(() => {
          const updatedAssignment = {
            ...currentAssignment,
            [fieldName]: userId,
          };

          this.assignment$.next(updatedAssignment);

          this.assignmentChangeHub.sendAssignmentChangeNotification(
            currentAssignment.id,
          );

          this.addEmployee(employee);
        }),
        finalize(() => {
          this.isLoading = false;
          this.hasUnsavedChanges = false;
        }),
      )
      .subscribe();
  }

  public override removeEmployee(employee: MinimalProfile): void {
    this.isLoading = true;

    const currentAssignment = this.assignment$.getValue();
    const fieldName = 'userId';
    const userId = null;

    this.assignmentService
      .patchAssignment(currentAssignment.id, fieldName, userId)
      .pipe(
        first(),
        tap(() => {
          const updatedAssignment = {
            ...currentAssignment,
            [fieldName]: userId,
          };

          this.assignment$.next(updatedAssignment);

          this.assignmentChangeHub.sendAssignmentChangeNotification(
            currentAssignment.id,
          );

          super.removeEmployee(employee);
        }),
        finalize(() => {
          this.isLoading = false;
          this.hasUnsavedChanges = false;
        }),
      )
      .subscribe();
  }

  public onDiscussionTabChange(tabId: string): void {
    if (
      tabId === this.discussionTabs.conversation ||
      tabId === this.discussionTabs.reports
    ) {
      this.activeDiscussionTab = tabId;
    }
  }

  public saveAssignmentChanges(): void {
    this.isLoading = true;

    const assignmentStatus = this.statusFormControl.value!;
    const assignmentForUpdate: AssignmentForUpdate = {
      title: this.titleFormControl.value!,
      description: this.descriptionFormControl.value || undefined,
      deadline: this.generateDeadline(
        this.deadlineFormControl.value,
        assignmentStatus,
      ),
      status: assignmentStatus,
      priority: this.assignmentFormModel.form.controls.priority.value,
      employeeId: this.assignmentEmployee?.id,
    };

    const assignmentId = this.assignment$.getValue().id;

    this.assignmentService
      .updateAssignment(assignmentId, assignmentForUpdate)
      .pipe(
        first(),
        tap(() => {
          if (assignmentForUpdate.description) {
            this.sanitizedDescription$.next(
              this.sanitizer.bypassSecurityTrustHtml(
                assignmentForUpdate.description,
              ),
            );
          }

          this.updateCurrentAssignment(assignmentForUpdate);

          this.assignmentChangeHub.sendAssignmentChangeNotification(
            assignmentId,
          );

          this.isLoading = false;
          this.hasUnsavedChanges = false;
        }),
        catchError((error) => {
          console.error(error);
          this.isLoading = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public deleteAssignment() {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
      this.translateService,
      `WARNINGS.DELETE`,
      {
        uk: 'задачу',
        en: 'assignment',
      },
    );

    const assignmentId = this.assignment$.getValue().id;

    modalRef.closed
      .pipe(
        first(),
        filter((res) => !!res),
        switchMap(() => this.assignmentService.deleteAssignment(assignmentId)),
        switchMap(() =>
          this.fileService.delete(
            `companies/${this.companyId}/projects/${this.projectId}/assignments/${assignmentId}`,
          ),
        ),
        tap(() => {
          this.assignmentChangeHub.sendAssignmentChangeNotification(
            assignmentId,
          );

          this.router.navigateByUrl(
            `companies/${this.companyId}/projects/${this.projectId}`,
          );
        }),
      )
      .subscribe();
  }

  public onAttachmentsFormChanged(value: boolean): void {
    this.hasUnsavedChanges = value;
  }

  public follow(): void {
    this.followService
      .addAssignmentFollower(this.assignment$.getValue().id)
      .pipe(
        first(),
        tap(() => (this.isFollowing = true)),
      )
      .subscribe();
  }

  public unfollow(): void {
    this.followService
      .removeAssignmentFollower(this.assignment$.getValue().id)
      .pipe(
        first(),
        tap(() => (this.isFollowing = false)),
      )
      .subscribe();
  }

  public archiveAssignment(assignmentId: string): void {
    this.assignmentService
      .archiveAssignment(assignmentId)
      .pipe(
        first(),
        tap(() => {
          const updatedAssignment = this.assignment$.getValue();
          updatedAssignment.isArchived = true;

          this.assignment$.next(updatedAssignment);
        }),
      )
      .subscribe();
  }

  public unarchiveAssignment(assignmentId: string): void {
    this.assignmentService
      .unarchiveAssignment(assignmentId)
      .pipe(
        first(),
        tap(() => {
          const updatedAssignment = this.assignment$.getValue();
          updatedAssignment.isArchived = false;

          this.assignment$.next(updatedAssignment);
        }),
      )
      .subscribe();
  }

  private updateCurrentAssignment(
    assignmentForUpdate: AssignmentForUpdate,
  ): void {
    const currentAssignment = this.assignment$.getValue();

    const updatedAssignment: AssignmentOverview = {
      id: currentAssignment.id,
      createdAt: currentAssignment.createdAt,
      startDate: currentAssignment.startDate,
      isArchived: currentAssignment.isArchived,
      project: { ...currentAssignment.project },
      ...assignmentForUpdate,
      employeeProfile: this.assignmentEmployee || undefined,
      producer: currentAssignment.producer,
    };

    if (
      updatedAssignment.deadline &&
      [AssignmentStatuses.Completed, AssignmentStatuses.Postponed].includes(
        updatedAssignment.status,
      )
    ) {
      const handledDate = new Date(assignmentForUpdate.deadline!);
      handledDate.setDate(handledDate.getDate() - 1);

      updatedAssignment.deadline = Util.getFormattedStringDate(handledDate);
    }

    if (updatedAssignment.deadline) {
      const deadlineDate = new Date(updatedAssignment.deadline);
      deadlineDate.setDate(deadlineDate.getDate() + 1);
      deadlineDate.setHours(0, 0, 0, 0);

      updatedAssignment.timeLeft = Convertor.getTimeDifferenceString(
        new Date(),
        new Date(deadlineDate.getTime() - 1000),
      );
    }

    if (assignmentForUpdate.status === AssignmentStatuses.Active) {
      updatedAssignment.startDate = new Date();
    }

    if (assignmentForUpdate.status === AssignmentStatuses.New) {
      updatedAssignment.startDate = undefined;
    }

    this.assignment$.next(updatedAssignment);
  }

  private initEmployee$(
    assignment$: Observable<AssignmentOverview>,
  ): Observable<MinimalProfile | undefined> {
    return assignment$.pipe(
      map((assignment) => assignment.employeeProfile),
      filter((x) => !!x),
      switchMap((employeeProfile) =>
        ObservableUtil.getProfileWithImage$(employeeProfile, this.fileService),
      ),
    );
  }

  private generateDeadline(
    deadlineDate: string | Date | null,
    assignmentStatus: AssignmentStatuses,
  ): string | undefined {
    if (
      !deadlineDate &&
      ![AssignmentStatuses.Completed, AssignmentStatuses.Postponed].includes(
        assignmentStatus,
      )
    ) {
      return undefined;
    }

    let handledDate: Date;
    if (
      !deadlineDate &&
      [AssignmentStatuses.Completed, AssignmentStatuses.Postponed].includes(
        assignmentStatus,
      )
    ) {
      handledDate = Util.getNextDayFromDate(new Date());
    } else {
      handledDate = Util.getNextDayFromDate(new Date(deadlineDate!));
    }

    return Util.getFormattedStringDate(handledDate);
  }
}
